import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { useSetAusgeblendet } from "../../hooks/useCustomers.js";

/**
 * Ein Bereichs-Chip in der Kundenliste, mit dem Knopf zum Aus- und
 * Einblenden daneben.
 *
 * Ausgeblendet heißt: Diese (Kunde × Bereich)-Zelle verschwindet für ALLE aus
 * Listen und Auswahlfeldern. Der Chip zeigt den Umstand und den Grund, damit
 * niemand im Audit nachsehen muss, um zu verstehen, warum eine Zeile fehlt.
 *
 * **Der zugängliche Name nennt Kunde und Bereich** — in einer Liste entstehen
 * Namensdubletten von selbst, sobald sie mehr als eine Zeile hat.
 */
export function SegmentChip({
  customerId,
  customerName,
  segmentId,
  segmentName,
  ausgeblendet,
  grund,
  darfPflegen,
}: {
  customerId: string;
  customerName: string;
  segmentId: string;
  segmentName: string;
  ausgeblendet: boolean;
  grund: string | null;
  /** Nur Admin, GL und die Leitung DIESES Bereichs dürfen; der Server prüft es erneut. */
  darfPflegen: boolean;
}) {
  const { t } = useTranslation();
  const setAusgeblendet = useSetAusgeblendet();
  const [dialogOffen, setDialogOffen] = useState(false);
  const [grundEingabe, setGrundEingabe] = useState("");

  const chip = (
    <Badge
      color={ausgeblendet ? "gray" : "blue"}
      variant={ausgeblendet ? "outline" : "light"}
      size="sm"
      styles={ausgeblendet ? { label: { textDecoration: "line-through" } } : undefined}
    >
      {segmentName}
    </Badge>
  );

  const label = ausgeblendet
    ? t("pages.customers.einblendenFuer", { name: customerName, segment: segmentName })
    : t("pages.customers.ausblendenFuer", { name: customerName, segment: segmentName });

  return (
    <>
      <Group gap={2} wrap="nowrap">
        {ausgeblendet ? (
          <Tooltip
            label={
              grund
                ? t("pages.customers.ausgeblendetMitGrund", { grund })
                : t("pages.customers.ausgeblendetOhneGrund")
            }
            withArrow
          >
            {chip}
          </Tooltip>
        ) : (
          chip
        )}
        {darfPflegen && (
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            title={label}
            aria-label={label}
            loading={setAusgeblendet.isPending}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (ausgeblendet) {
                // Einblenden braucht keinen Grund und keine Rückfrage — es
                // stellt den Normalzustand wieder her.
                setAusgeblendet.mutate({ customerId, segmentId, ausgeblendet: false });
                return;
              }
              setGrundEingabe("");
              setDialogOffen(true);
            }}
          >
            {ausgeblendet ? <IconEye size={14} /> : <IconEyeOff size={14} />}
          </ActionIcon>
        )}
      </Group>

      <Modal
        opened={dialogOffen}
        onClose={() => setDialogOffen(false)}
        title={t("pages.customers.ausblendenTitel", {
          name: customerName,
          segment: segmentName,
        })}
      >
        <Stack gap="sm">
          {/* Die Folge beim Namen nennen, nicht beschönigen: Wer hier klickt,
              nimmt der Zelle auch ihre Wartungs- und Termin-Erinnerungen. */}
          <Text size="sm">{t("pages.customers.ausblendenHinweis")}</Text>
          <TextInput
            label={t("pages.customers.ausblendenGrund")}
            description={t("pages.customers.ausblendenGrundFreiwillig")}
            placeholder={t("pages.customers.ausblendenGrundBeispiel")}
            value={grundEingabe}
            maxLength={500}
            onChange={(e) => setGrundEingabe(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDialogOffen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={setAusgeblendet.isPending}
              onClick={() => {
                setAusgeblendet.mutate(
                  {
                    customerId,
                    segmentId,
                    ausgeblendet: true,
                    ...(grundEingabe.trim() ? { grund: grundEingabe.trim() } : {}),
                  },
                  { onSuccess: () => setDialogOffen(false) },
                );
              }}
            >
              {t("pages.customers.ausblendenBestaetigen")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
